import { useEffect, useState } from "react";
import { api, API_BASE } from "../lib/api";
import { PageHeader, Section } from "../components/Primitives";
import Pagination, { usePagination } from "../components/Pagination";
import { useApiLoading, LoadingRow } from "../components/TableLoader";
import { Download, Mail } from "lucide-react";

export default function ExportHistory() {
    const [exports, setExports] = useState([]);
    const [emails, setEmails] = useState([]);
    const apiLoading = useApiLoading();

    useEffect(() => {
        api.get("/exports").then((r) => setExports(r.data));
        api.get("/email-logs").then((r) => setEmails(r.data));
    }, []);

    const expPager = usePagination(exports, 25);
    const emailPager = usePagination(emails, 25);

    return (
        <div data-testid="exports-page">
            <PageHeader eyebrow="History" title="Export history" subtitle="Every generated export, who triggered it, where it went." />

            <Section title="Generated exports" testId="section-exports">
                <div className="bg-white border border-zinc-200 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-zinc-100 border-b border-zinc-200">
                                <th className="overline text-left p-3">Date</th>
                                <th className="overline text-left p-3">Type</th>
                                <th className="overline text-left p-3">Format</th>
                                <th className="overline text-left p-3">Rows</th>
                                <th className="overline text-left p-3">Status</th>
                                <th className="overline text-right p-3">Download</th>
                            </tr>
                        </thead>
                        <tbody>
                            {expPager.slice.map((e) => (
                                <tr key={e.id} className="border-b border-zinc-200 last:border-0">
                                    <td className="p-3 text-zinc-700 text-xs">{new Date(e.created_at).toLocaleString()}</td>
                                    <td className="p-3 capitalize text-zinc-900">{e.report_type}</td>
                                    <td className="p-3 uppercase text-zinc-700">{e.file_format}</td>
                                    <td className="p-3 tabular-nums text-zinc-700">{e.rows}</td>
                                    <td className="p-3">
                                        <span className={`px-2 py-0.5 rounded text-[10px] uppercase ${e.status === "emailed" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : e.status === "failed" ? "bg-red-500/10 text-red-400 border border-red-500/20" : "bg-amber-500/10 text-amber-400 border border-amber-500/20"}`}>{e.status}</span>
                                    </td>
                                    <td className="p-3 text-right">
                                        <a href={`${API_BASE}/exports/${e.id}/download`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-zinc-700 hover:wl-accent-text" data-testid={`download-${e.id}`}>
                                            <Download className="h-3 w-3" />download
                                        </a>
                                    </td>
                                </tr>
                            ))}
                            {!exports.length && (apiLoading ? <LoadingRow colSpan={6} /> : <tr><td colSpan={6} className="p-10 text-center text-zinc-500">No exports generated yet.</td></tr>)}
                        </tbody>
                    </table>
                    {exports.length > 0 && (
                        <Pagination
                            page={expPager.page}
                            totalPages={expPager.totalPages}
                            pageSize={expPager.pageSize}
                            range={expPager.range}
                            setPage={expPager.setPage}
                            setPageSize={expPager.setPageSize}
                            dataTestId="exports-pagination"
                        />
                    )}
                </div>
            </Section>

            <Section title="Email delivery log" testId="section-emails">
                <div className="bg-white border border-zinc-200 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-zinc-100 border-b border-zinc-200">
                                <th className="overline text-left p-3">Time</th>
                                <th className="overline text-left p-3">Recipient</th>
                                <th className="overline text-left p-3">Subject</th>
                                <th className="overline text-left p-3">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {emailPager.slice.map((m) => (
                                <tr key={m.id} className="border-b border-zinc-200 last:border-0">
                                    <td className="p-3 text-zinc-500 text-xs">{new Date(m.created_at).toLocaleString()}</td>
                                    <td className="p-3 text-zinc-800">{m.recipient}</td>
                                    <td className="p-3 text-zinc-700 truncate max-w-[420px]">{m.subject}</td>
                                    <td className="p-3">
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase ${m.status === "sent" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : m.status === "mocked" ? "bg-amber-500/10 text-amber-400 border border-amber-500/20" : "bg-red-500/10 text-red-400 border border-red-500/20"}`}>
                                            <Mail className="h-3 w-3" />{m.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {!emails.length && (apiLoading ? <LoadingRow colSpan={4} /> : <tr><td colSpan={4} className="p-10 text-center text-zinc-500">No email events yet.</td></tr>)}
                        </tbody>
                    </table>
                    {emails.length > 0 && (
                        <Pagination
                            page={emailPager.page}
                            totalPages={emailPager.totalPages}
                            pageSize={emailPager.pageSize}
                            range={emailPager.range}
                            setPage={emailPager.setPage}
                            setPageSize={emailPager.setPageSize}
                            dataTestId="emails-pagination"
                        />
                    )}
                </div>
            </Section>
        </div>
    );
}
